import { useField, useFormikContext } from 'formik';
import { memo, useRef, useCallback, useEffect } from 'react';
import { ErrorLevel } from '../../../../../API/Enums';
import { SelectCloseReason } from '../Select2/Select2.types';
import { FormSelect2Props } from './FormSelect2.types';
import { GMSelect2 } from '../..';

/**
 * Select for use in a Formik.
 *
 * Sets the value, error, onChange & helperText internally
 */
const FormSelect2: React.FC<FormSelect2Props> = memo(({
    validateOnChange,
    validateBeforeSubmit,
    onChange: propsOnChange,
    onClose: propsOnClose,
    helperText,
    warningText,
    ...props
}) => {
    const [field, meta] = useField(props.name);
    // we don't use helpers above since those references change every render
    const { validateField, setFieldValue, setFieldTouched, submitCount, validateOnBlur } = useFormikContext();
    const prevValueRef = useRef<any>(field.value);

    const onChange = useCallback((value: string) => {
        if (propsOnChange) {
            propsOnChange(value);
        }
        else {
            setFieldValue(props.name, value);
        }
    }, [props.name, propsOnChange, setFieldValue]);

    const onClose = useCallback((reason: SelectCloseReason) => {
        setFieldTouched(props.name, true, validateOnBlur && reason !== 'selection');

        if (propsOnClose) {
            propsOnClose(reason);
        }
    }, [propsOnClose, setFieldTouched, props.name, validateOnBlur]);

    useEffect(() => {
        if (validateOnChange && meta.touched && field.value !== prevValueRef.current) {
            validateField(props.name);
        }

        prevValueRef.current = field.value;
    }, [field.value, meta.touched, props.name, validateOnChange, validateField]);

    const getErrorLevel = (): ErrorLevel => {
        if (!!meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return ErrorLevel.Error;
        }

        if (!!warningText) {
            return ErrorLevel.Warning;
        }

        return ErrorLevel.None;
    };

    const getHelperText = (): FormSelect2Props['helperText'] => {
        if (meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return meta.error;
        }

        if (warningText) {
            return warningText;
        }

        return helperText;
    };

    return (
        <GMSelect2
            value={field.value?.toString()}
            onChange={onChange}
            error={getErrorLevel()}
            helperText={getHelperText()}
            onClose={onClose}
            {...props}
        />
    );
});

export default FormSelect2;